// One RFQ, part or supplier opened beside the list: the same summary strip as
// a conversation, then its supplier conversations as clean rows. A row opens
// that conversation.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/GroupPanel.tsx

import React, { useState } from 'react';
import { Box, Button, Collapse, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Close as CloseIcon, ExpandMore as MoreIcon } from '@mui/icons-material';
import { CenterRow } from '../../../services/rfqCenterService';
import { CenterGroup, CenterTab, TAB_LABEL, askedLine, quoteLine, stateColors } from './centerView';

interface GroupPanelProps {
  group: CenterGroup;
  tab: Exclude<CenterTab, 'inbox'>;
  onOpen: (rfqId: string) => void;
  onClose: () => void;
}

const LABEL_SX = { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'text.disabled' } as const;

const GroupPanel: React.FC<GroupPanelProps> = ({ group, tab, onOpen, onClose }) => {
  const theme = useTheme();
  const [showDrafts, setShowDrafts] = useState(false);
  const active = group.rows.filter((r) => r.state !== 'draft');
  const drafts = group.rows.filter((r) => r.state === 'draft');
  const lead = group.lead;
  const cells: Array<{ label: string; value: string; hot?: boolean }> = [
    tab === 'suppliers' ? { label: 'Open with them', value: `${active.length} ${active.length === 1 ? 'RFQ' : 'RFQs'}` } : { label: 'Asked', value: `${group.counts.asked} ${group.counts.asked === 1 ? 'supplier' : 'suppliers'}` },
    { label: 'Replied', value: `${group.counts.replied} of ${group.counts.asked}` },
    { label: 'Best quote', value: group.bestQuote ?? 'None yet' },
    { label: 'Needs you', value: group.counts.you ? `${group.counts.you} ${group.counts.you === 1 ? 'conversation' : 'conversations'}` : 'Nothing', hot: group.counts.you > 0 },
  ];

  const memberRow = (row: CenterRow) => {
    const c = stateColors(row);
    return (
      <Box
        key={row.rfqId}
        component="button"
        type="button"
        onClick={() => onOpen(row.rfqId)}
        sx={{
          font: 'inherit',
          color: 'inherit',
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${c.rail}`,
          borderRadius: 2.5,
          px: 2,
          py: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 2fr) auto 70px' },
          gap: 2,
          alignItems: 'center',
          transition: 'box-shadow .12s',
          '&:hover': { boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}` },
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {tab === 'suppliers' ? askedLine(row) : row.supplier.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {row.quote?.unitPrice !== undefined ? quoteLine(row) : row.rfqNumber}
          </Typography>
        </Box>
        <Typography dir="auto" variant="body2" color="text.secondary" sx={{ minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>
          {row.latest?.text ?? (row.state === 'draft' ? 'Not sent' : 'No reply yet')}
        </Typography>
        <Box component="span" sx={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 24, px: 1.25, borderRadius: 1, border: `1px solid ${c.color}`, color: c.color, bgcolor: c.bg, fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {row.chip}
        </Box>
        <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: row.stale ? 700 : 500, color: row.stale ? theme.palette.error.main : 'text.secondary' }}>
          {row.waitingOn.days !== undefined ? `${row.waitingOn.days} d` : ''}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ px: 2.5, py: 1, alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="body2" fontWeight={700}>
          {TAB_LABEL[tab]}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} startIcon={<CloseIcon fontSize="small" />} sx={{ color: 'text.secondary' }}>
          Close
        </Button>
      </Stack>

      <Box sx={{ px: 2.5, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
          {group.title}
        </Typography>
        {group.subtitle && (
          <Typography variant="caption" color="text.secondary">
            {group.subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ mx: 2.5, mt: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, minmax(0, 1fr))' }, overflow: 'hidden' }}>
        {cells.map((c) => (
          <Box key={c.label} sx={{ px: 1.75, py: 1.25, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`, mr: '-1px', mb: '-1px' }}>
            <Typography sx={LABEL_SX}>{c.label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: c.hot ? theme.palette.primary.main : 'text.primary' }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2.5, pt: 3, pb: 2.5 }}>
        <Typography sx={{ ...LABEL_SX, mb: 1.25 }}>{tab === 'suppliers' ? `RFQs with ${group.title}` : 'Suppliers'}</Typography>
        {active.length ? (
          <Stack spacing={1}>{active.map(memberRow)}</Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing has been sent yet.
          </Typography>
        )}
        {drafts.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" onClick={() => setShowDrafts((v) => !v)} endIcon={<MoreIcon sx={{ transform: showDrafts ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', px: 0.5 }}>
              {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'} not sent
            </Button>
            <Collapse in={showDrafts} unmountOnExit>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {drafts.map(memberRow)}
              </Stack>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GroupPanel;
